import {
  FC,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DateTime } from 'luxon';
import { TextField } from '../TextField';
import { DATE_FORMAT_WITH_TIME, DATE_FORMAT_WITHOUT_TIME } from './constants';
import { Button } from '../../buttons/Button/Button';
import { Calendar, CircleX } from '../../icons';
import { useColor } from '../../../hooks/useColor';
import { RenderWhen } from '../../RenderWhen';
import { CalendarGrid, CalendarPopover, TimeInput } from './components';
import { copyTime, getDayTimestamp, startOfMonth } from './DateInput.helpers';
import { DateInputChangeEvent, DateInputProps, DateInputValue } from './DateInput.types';

export type {
  DateInputChangeEvent,
  DateInputProps,
  DateInputRange,
  DateInputValue,
} from './DateInput.types';

const noop = () => {};

export const DateInput: FC<DateInputProps> = ({
  'aria-label': ariaLabel,
  testId = 'date__input',
  iconOnly = false,
  iconPosition = 'left',
  iconColor,
  placeholderText,
  required,
  disabled,
  label,
  startDate,
  endDate,
  selected,
  selectsRange,
  error,
  shouldCloseOnSelect = false,
  showTimeInput = false,
  startOpen = false,
  minDate,
  maxDate,
  popperPlacement = 'bottom-left',
  onCalendarClose = noop,
  errorMessage,
  typographyClassName,
  isClearable = false,
  name,
  onBlur = noop,
  onClear = noop,
  onChange = noop,
  textFieldEndContent,
  iconColorClassName,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const skipNextFocusOpenRef = useRef(false);
  const calendarColor = useColor({ color: iconColor ?? 'primary' });
  const calendarId = `date-input-calendar-${useId()}`;
  const effectiveStartDate = startDate ?? selected;
  const [isOpen, setIsOpen] = useState(startOpen && !disabled);
  const [visibleMonth, setVisibleMonth] = useState(() =>
    startOfMonth(effectiveStartDate ?? new Date()),
  );
  const isCalendarOpen = isOpen && !disabled;

  const closeCalendar = useCallback(
    (restoreFocus = false) => {
      if (!isOpen) return;
      setIsOpen(false);
      onCalendarClose();
      if (restoreFocus) {
        const trigger = containerRef.current?.querySelector<HTMLElement>(
          '[data-date-input-trigger]',
        );
        skipNextFocusOpenRef.current = trigger instanceof HTMLInputElement;
        trigger?.focus();
      }
    },
    [isOpen, onCalendarClose],
  );

  const openCalendar = useCallback(() => {
    if (disabled) return;
    setVisibleMonth(startOfMonth(effectiveStartDate ?? new Date()));
    setIsOpen(true);
  }, [disabled, effectiveStartDate]);

  useEffect(() => {
    if (!disabled || !isOpen) return undefined;

    const timeoutId = window.setTimeout(closeCalendar, 0);
    return () => window.clearTimeout(timeoutId);
  }, [closeCalendar, disabled, isOpen]);

  useEffect(() => {
    if (!isCalendarOpen) return undefined;

    const handleOutsideMouseDown = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;
      const calendar = document.getElementById(calendarId);
      if (!containerRef.current?.contains(target) && !calendar?.contains(target)) closeCalendar();
    };
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') closeCalendar(true);
    };

    document.addEventListener('mousedown', handleOutsideMouseDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideMouseDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [calendarId, closeCalendar, isCalendarOpen]);

  const formattedDate = useMemo(() => {
    if (selectsRange && startDate && endDate) {
      return `${DateTime.fromJSDate(startDate).toFormat(
        DATE_FORMAT_WITHOUT_TIME,
      )} - ${DateTime.fromJSDate(endDate).toFormat(DATE_FORMAT_WITHOUT_TIME)}`;
    }

    if (!selectsRange && effectiveStartDate) {
      return DateTime.fromJSDate(effectiveStartDate).toFormat(
        showTimeInput ? DATE_FORMAT_WITH_TIME : DATE_FORMAT_WITHOUT_TIME,
      );
    }

    return '';
  }, [selectsRange, startDate, endDate, effectiveStartDate, showTimeInput]);

  const calendarIcon = (
    <Calendar role="img" aria-label="Calendar" className={iconColorClassName} color={calendarColor}>
      <title>Calendar</title>
    </Calendar>
  );

  /**
   * we are doing this because of super dispatch because customers are
   * not receiving correct times. This should be supers problem to fix but for now
   * we are just going to do it. When we get customer portal we will remove this as
   * we won't care about super anymore
   */
  const setDateTo6 = (d: Date | null, variant: 'am' | 'pm') => {
    d?.setHours(variant === 'am' ? 6 : 18);
    d?.setMinutes(0);
    d?.setMilliseconds(0);
  };

  const handleChange = (date: DateInputValue, event?: DateInputChangeEvent) => {
    // if event exists, this means a user selected a date from calendar
    // if event does not exists, this means a user changed the time via time input.
    // this will allow us to set 6am/6pm on date selection but still allow time to be modified
    if (event) {
      if (Array.isArray(date)) {
        setDateTo6(date[0], 'am');
        setDateTo6(date[1], 'pm');
      }
      // do not adjust time if user has already selected a time
      else if (!startDate) {
        setDateTo6(date, 'am');
      }
    }
    onChange(date, event);
  };

  const handleDateSelect = (
    date: Date,
    event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (!selectsRange) {
      handleChange(copyTime(date, effectiveStartDate), event);
      if (shouldCloseOnSelect) closeCalendar(true);
      return;
    }

    if (!startDate || endDate || getDayTimestamp(date) < getDayTimestamp(startDate)) {
      handleChange([copyTime(date), null], event);
      return;
    }

    if (getDayTimestamp(date) === getDayTimestamp(startDate)) {
      handleChange([copyTime(date, startDate), null], event);
      return;
    }

    handleChange([new Date(startDate), copyTime(date)], event);
    if (shouldCloseOnSelect) closeCalendar(true);
  };

  const handleTimeChange = (date: Date) => {
    handleChange(date);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openCalendar();
    }
  };

  const handleInputFocus = () => {
    if (skipNextFocusOpenRef.current) {
      skipNextFocusOpenRef.current = false;
      return;
    }
    openCalendar();
  };

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className={iconOnly ? 'relative w-[34px]' : 'relative w-full'}
    >
      {iconOnly ? (
        <button
          type="button"
          data-date-input-trigger
          aria-label={ariaLabel ?? 'Choose date'}
          aria-controls={calendarId}
          aria-expanded={isCalendarOpen}
          aria-haspopup="dialog"
          disabled={disabled}
          onClick={openCalendar}
          className="grid size-[34px] cursor-pointer place-items-center rounded-sm border-none bg-common-white shadow-secondary-inset disabled:cursor-not-allowed disabled:bg-bg-secondary"
        >
          {calendarIcon}
        </button>
      ) : (
        <TextField
          data-date-input-trigger
          aria-label={ariaLabel}
          aria-controls={calendarId}
          aria-expanded={isCalendarOpen}
          aria-haspopup="dialog"
          name={name}
          typographyClassName={typographyClassName}
          required={required}
          disabled={disabled}
          icon={calendarIcon}
          iconPosition={iconPosition}
          label={label}
          value={formattedDate}
          readOnly
          error={error}
          errorMessage={errorMessage}
          placeholder={placeholderText}
          onBlur={onBlur}
          onClick={openCalendar}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          endContent={
            <div className="flex gap-4">
              {textFieldEndContent}
              <RenderWhen condition={Boolean(!disabled && isClearable && effectiveStartDate)}>
                <Button variant="transparent" onClick={onClear} aria-label="Clear date">
                  <CircleX data-testid="close-circle-icon" className="size-5 stroke-primary-400" />
                </Button>
              </RenderWhen>
            </div>
          }
        />
      )}

      {isCalendarOpen && (
        <CalendarPopover id={calendarId} placement={popperPlacement} anchorRef={containerRef}>
          <CalendarGrid
            visibleMonth={visibleMonth}
            onVisibleMonthChange={setVisibleMonth}
            selected={selected}
            startDate={startDate}
            endDate={endDate}
            selectsRange={selectsRange}
            minDate={minDate}
            maxDate={maxDate}
            onDateSelect={handleDateSelect}
          />
          {showTimeInput && !selectsRange && (
            <TimeInput date={effectiveStartDate} onChange={handleTimeChange} />
          )}
        </CalendarPopover>
      )}
    </div>
  );
};

export default DateInput;
